import React, { useEffect } from 'react'
import { AllowedAccess } from 'react-permission-role'
import { useLocation, useParams } from 'react-router-dom'
import { Card, TextField, CardContent, Button, FormControl, Grid, MenuItem } from '@mui/material';

import {
    LinearLoading,
    CustomAutocomplete
} from '../../../../../common'

import { useFetchInitManagementForm } from './hooks'

import {
    InfoApproval,
    TransferToCentral,
    TransferToBank,
    CashReceipt,
    CashRefill,
    ServicePayment,
    BuyingSellingDollars
} from './sections'

const FORM_COMPONENTS_TO_RENDER = {
    // Cajas Administrativas
    1: TransferToCentral, // TRASLADO DE EFECTIVO A CAJA CENTRAL
    2: TransferToBank, // TRASLADO DE EFECTIVO A CUENTA BANCARIA
    3: CashReceipt, // INGRESO DE EFECTIVO
    6: BuyingSellingDollars, // COMPRA Y VENTA DE DOLRES
    // Cajas Chicas
    4: CashRefill, // REINTEGRO DE EFECTIVO
    5: ServicePayment, // PAGO DE SERVICIOS
    7: BuyingSellingDollars, // COMPRA Y VENTA DE DOLRES
}

export const CashierManagementForm = () => {

    const location = useLocation();
    const paramsURL = useParams();
    const useFetchInit = useFetchInitManagementForm({location, paramsURL})

    const {
        Forms,
        FetchData,
        Actions
    } = useFetchInit

    const { FormScreen } = Forms
    const { formState, onInputChange, onSelectSearchChange } = FormScreen
    
    const {
        fetchSpecificManagement,
        fetchCategories,
        fetchAllCashRegisters,
        fetchManagementType
    } = FetchData
    const { loadingFetchOperationDetail } = fetchSpecificManagement
    const { dataCategories, loadingFetchCategories } = fetchCategories
    const { dataCashRegisters, loadingFetchCashRegisters } = fetchAllCashRegisters
    const { dataManagemetType, loadingFetchManagementType } = fetchManagementType

    const { onSubmitForm, handleClickReturnMainScreen } = Actions

    useEffect(() => {
            
        fetchAllCashRegisters.setParamsToSearch({
            idcategoria: formState.idcategoria,
            currency_type: formState.currency_type
        })
        fetchAllCashRegisters.executeFetch()
        
        fetchManagementType.setParamsToSearch({
            idcategoria: formState.idcategoria
        })
        fetchManagementType.executeFetch()

    }, [formState.idcategoria, formState.currency_type])

    // ------------------------------------------------ Subtipo seleccionado
    const SelectedFormComponent = FORM_COMPONENTS_TO_RENDER[formState.idgestiontipo] || null

    const readOnlyMode = paramsURL.idgestion && paramsURL.idgestion > 0 && paramsURL.idgestion !== ''

    return (
        <AllowedAccess permissions={['247_GESTIONES']} renderAuthFailed={<p>Error</p>}>
            <Card style={{zIndex:'1'}}>
                <CardContent>
                    <div className='h4 mt-1 mb-1'>
                        {formState.idgestion > 0
                            ? 'Visualizar Registro'
                            : 'Crear Registro'}
                    </div>
                    <div className="border-bottom border-dark"></div>
                    {loadingFetchOperationDetail && ( <LinearLoading /> )}

                    <div className="pt-3"></div>
                    <p className='text-danger'>* campos obligatorios</p>
                    <div className="pt-2"></div>

                    <form onSubmit={onSubmitForm}>
                        <Grid container columnSpacing={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <FormControl fullWidth>
                                    <label htmlFor="idcategoria">* Categoría</label>
                                    {(loadingFetchCategories)&&(<LinearLoading/>)}
                                    <CustomAutocomplete 
                                        customId="idcategoria"
                                        customName="idcategoria"
                                        customValue={formState.idcategoria}
                                        customOnChange={(target, value) => onSelectSearchChange({ target: { name: 'idcategoria', value: value.value }})}
                                        customPlaceholder='Seleccionar opción'
                                        customOptions={
                                            dataCategories
                                                .filter(({ idcategoria }) => readOnlyMode || idcategoria === 3)
                                                .map(({ idcategoria, nombre }) => ({
                                                    value: idcategoria,
                                                    name: nombre
                                                }))
                                        }
                                        customDisableClearable={true}
                                        customDisabled={readOnlyMode}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} lg={6}>
                                <FormControl fullWidth>
                                    <label htmlFor="currency_type">* Tipo</label>
                                    <TextField
                                        id="currency_type"
                                        name="currency_type"
                                        value={formState.currency_type}
                                        onChange={onInputChange}
                                        select
                                        size="small"
                                        variant="outlined"
                                        hiddenLabel
                                        InputProps={{ 
                                            readOnly: readOnlyMode
                                        }}
                                    >
                                        <MenuItem value='QUETZAL'>QUETZAL</MenuItem>
                                        <MenuItem value='DOLAR'>DOLAR</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <div className="pt-4"></div>
                        <Grid container columnSpacing={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <FormControl fullWidth>
                                    <label htmlFor="idcaja">* Caja</label>
                                    {(loadingFetchCashRegisters)&&(<LinearLoading/>)}
                                    <CustomAutocomplete 
                                        customId="idcaja"
                                        customName="idcaja"
                                        customValue={formState.idcaja}
                                        customOnChange={(target, value) => onSelectSearchChange({ target: { name: 'idcaja', value: value.value }})}
                                        customPlaceholder='Seleccionar opción'
                                        customOptions={
                                            dataCashRegisters.map(({ idcaja, descripcion_corta, currency_type }) => ({
                                                value: idcaja,
                                                name: `${descripcion_corta} - ${currency_type}`
                                            }))
                                        }
                                        customDisableClearable={true}
                                        customDisabled={readOnlyMode}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                                <FormControl fullWidth>
                                    <label htmlFor="idgestiontipo">* Gestión</label>
                                    {(loadingFetchManagementType)&&(<LinearLoading/>)}
                                    <CustomAutocomplete 
                                        customId="idgestiontipo"
                                        customName="idgestiontipo"
                                        customValue={formState.idgestiontipo}
                                        customOnChange={(target, value) => onSelectSearchChange({ target: { name: 'idgestiontipo', value: value.value }})}
                                        customPlaceholder='Seleccionar opción'
                                        customOptions={
                                            dataManagemetType.map(({ idgestiontipo, nombre }) => ({
                                                value: idgestiontipo,
                                                name: nombre
                                            }))
                                        }
                                        customDisableClearable={true}
                                        customDisabled={readOnlyMode}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <div className="pt-4"></div>
                        {(SelectedFormComponent)&&(
                            <SelectedFormComponent
                                useFetchInit={useFetchInit}
                                readOnlyMode={readOnlyMode}
                            />
                        )}

                        <div className="pt-4"></div>
                        <Grid container columnSpacing={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
                            <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mb: 1, mt: 2 }}>
                                <FormControl fullWidth>
                                    <label htmlFor="descripcion">Descripción</label>
                                    <TextField
                                        id='descripcion'
                                        name='descripcion'
                                        value={ formState.descripcion } 
                                        onChange={ onInputChange }
                                        size='small'
                                        placeholder='Comentarios'
                                        type='text'
                                        multiline
                                        rows={5}
                                        variant='outlined'
                                        InputProps={{
                                            readOnly: readOnlyMode
                                        }}
                                        
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        {(
                            paramsURL.idgestion
                            && paramsURL.idgestion > 0
                            && paramsURL.idgestion !== ''
                        )&&(
                            <InfoApproval useFetchInit={useFetchInit}/>
                        )}

                        <div className="pt-4"></div>
                        <Grid container>
                            <Grid item>
                                <div className='update ml-auto mr-auto'>
                                    <Button
                                        style={{background: "#4b4b4c"}}
                                        variant="contained"
                                        type='submit'
                                    >
                                        <i className="fa-regular fa-floppy-disk"></i> &nbsp; Guardar
                                    </Button>
                                    &nbsp; &nbsp;
                                    <Button
                                        style={{ marginRight: 2, marginLeft: 2, background: "#ef8157" }}
                                        type='button'
                                        variant="contained"
                                        onClick={handleClickReturnMainScreen}
                                    >
                                        <i className="fa-regular fa-circle-left"></i> &nbsp; Cancelar
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </AllowedAccess>
    )
}
